import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, MessageCircle, FileText, Printer } from "lucide-react";
import { fmt } from "../../../../../services/erpApi";
import StageBar, { INVOICE_STAGES } from "../../../../../components/admin/StageBar";
import CustomerSelect from "../../../../../components/admin/CustomerSelect";

/* ------------------------------------------------------------------ */
/* Smart button Odoo                                                  */
/* ------------------------------------------------------------------ */
function SmartButton({ icon: Icon, count, label, onClick, color = "#714b67" }) {
  const styles = {
    btn: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      minWidth: 160,
      padding: "10px 16px",
      border: 0,
      borderLeft: "1px solid #e0e4e7",
      background: "#fff",
      cursor: "pointer",
      textAlign: "left",
      fontSize: 13,
      color: "#263746",
      transition: "background .15s",
    },
    btnHover: { background: "#f6f7f8" },
    icon: { color, flexShrink: 0 },
    text: { display: "flex", flexDirection: "column", lineHeight: 1.2 },
    count: { fontSize: 16, fontWeight: 600, color },
    label: { fontSize: 11, color: "#63758a" },
  };
  const [hover, setHover] = React.useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ ...styles.btn, ...(hover ? styles.btnHover : {}) }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Icon size={22} style={styles.icon} strokeWidth={1.6} />
      <span style={styles.text}>
        {count != null && <span style={styles.count}>{count}</span>}
        <span style={styles.label}>{label}</span>
      </span>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Styles Odoo                                                        */
/* ------------------------------------------------------------------ */
const s = {
  page: { minHeight: "100vh", background: "#f6f7f8", color: "#263746", fontFamily: '"Lucida Grande", Helvetica, Verdana, Arial, sans-serif' },
  topbar: { minHeight: 31, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 14px", background: "#fff", borderBottom: "1px solid #d8dde2", fontSize: 13 },
  breadcrumb: { display: "flex", alignItems: "center", gap: 8, color: "#63758a" },
  back: { display: "inline-flex", alignItems: "center", gap: 5, border: 0, background: "transparent", color: "#63758a", cursor: "pointer", padding: 0, fontSize: 12 },
  btnPrimary: { display: "inline-flex", gap: 5, alignItems: "center", border: 0, background: "#6594ad", color: "#fff", padding: "7px 12px", fontSize: 11, cursor: "pointer" },
  btnGhost: { border: 0, background: "transparent", color: "#714b67", cursor: "pointer", fontSize: 11 },
  statusSteps: { marginLeft: "auto", display: "flex", color: "#97a0a8", fontSize: 10 },
  statusStep: { padding: "8px 16px", borderLeft: "1px solid #d8dde2", background: "#fff" },
  statusStepActive: { padding: "8px 16px", background: "#263d4a", color: "#fff" },
  sheet: { width: "min(100%, 1008px)", margin: "0 auto", padding: "30px 24px 36px", boxSizing: "border-box", background: "#fff", border: "1px solid #e0e4e7", boxShadow: "0 1px 3px rgba(0,0,0,.04)" },
  sheetTitle: { margin: "0 0 18px", fontSize: 20, fontWeight: 600, color: "#263746" },
  tabs: { display: "flex", gap: 18, borderBottom: "1px solid #dce1e5", marginTop: 28, paddingBottom: 8, color: "#6c7b88", fontSize: 11 },
  tab: { cursor: "pointer", padding: "4px 2px", borderBottom: "2px solid transparent" },
  tabActive: { cursor: "pointer", padding: "4px 2px", color: "#263746", fontWeight: 600, borderBottom: "2px solid #714b67" },
  readonly: { borderBottom: "1px solid transparent", padding: "6px 4px", color: "#34495a", minHeight: 14 },
  input: { width: "100%", boxSizing: "border-box", border: 0, borderBottom: "1px solid #aeb8c1", padding: "6px 4px", outline: 0, color: "#34495a", background: "transparent", fontSize: 12 },
  sectionTitle: { fontSize: 11, fontWeight: 700, color: "#526170", textTransform: "uppercase", letterSpacing: ".4px", margin: "24px 0 8px", paddingBottom: 6, borderBottom: "1px solid #dce1e5" },
  cardGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginTop: 8 },
  card: { border: "1px solid #e0e4e7", background: "#fafbfc", padding: "10px 12px", fontSize: 11, color: "#405161", display: "flex", justifyContent: "space-between", gap: 10 },
  cardImg: { width: 46, height: 46, background: "#eceff2", border: "1px solid #dce1e5", flexShrink: 0 },
  amount: { fontWeight: 600 },
  jGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginTop: 8 },
  jCard: { border: "1px solid #e0e4e7", background: "#fff", padding: "10px 12px", fontSize: 11, color: "#405161", display: "flex", flexDirection: "column", gap: 6, minHeight: 88 },
  jAccount: { color: "#526170" },
  jLabel: { fontStyle: "italic", color: "#63758a" },
  jTaxLine: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "auto", borderTop: "1px solid #eef1f3", paddingTop: 6 },
  dot: { display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: "#6c7b88", marginRight: 5 },
  twoCol: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 48px" },
  fieldRow: { display: "grid", gridTemplateColumns: "150px 1fr", alignItems: "center", minHeight: 32, fontSize: 11, color: "#526170" },
  fieldLabel: { paddingRight: 8 },
  totals: { display: "flex", justifyContent: "flex-end", gap: 18, borderTop: "1px solid #dce1e5", paddingTop: 14, marginTop: 12, fontSize: 12 },
  terms: { marginTop: 18, fontSize: 11, color: "#6c7b88" },
  tableHead: { display: "grid", gridTemplateColumns: "1.4fr 1.6fr .6fr .9fr .7fr 28px", gap: 7, alignItems: "center", padding: "8px 6px", background: "#e9edf1", color: "#405161", fontSize: 10, fontWeight: 600, minWidth: 720 },
  tableRow: { display: "grid", gridTemplateColumns: "1.4fr 1.6fr .6fr .9fr .7fr 28px", gap: 7, alignItems: "center", padding: "5px 6px", borderBottom: "1px solid #edf0f2", minWidth: 720, color: "#405161", fontSize: 11 },
  cellInput: { width: "100%", boxSizing: "border-box", border: 0, borderBottom: "1px solid #c6cdd3", padding: "6px 3px", outline: 0, fontSize: 11, color: "#405161" },
  delete: { border: 0, background: "transparent", color: "#be123c", cursor: "pointer" },
};

/* ------------------------------------------------------------------ */
/* Champ Odoo réutilisable                                            */
/* ------------------------------------------------------------------ */
function Field({ label, editable, value, onChange, type = "text", placeholder, options, children }) {
  return (
    <label style={s.fieldRow}>
      <span style={s.fieldLabel}>{label}</span>
      {editable ? (
        type === "select" ? (
          <select value={value ?? ""} onChange={(e) => onChange?.(e.target.value)} style={s.input}>
            <option value="">{placeholder || "—"}</option>
            {options?.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        ) : type === "checkbox" ? (
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input type="checkbox" checked={!!value} onChange={(e) => onChange?.(e.target.checked)} />
            {placeholder}
          </span>
        ) : (
          <input
            type={type}
            value={value ?? ""}
            placeholder={placeholder}
            onChange={(e) => onChange?.(e.target.value)}
            style={s.input}
          />
        )
      ) : (
        <span style={s.readonly}>{children ?? (value === "" || value == null ? "—" : value)}</span>
      )}
    </label>
  );
}

/* ------------------------------------------------------------------ */
/* Composant principal                                                */
/* ------------------------------------------------------------------ */
export default function CustomerInvoiceView({
  invoice,
  editable = false,
  form,
  setForm,
  partners = [],
  products = [],
  journals = [],
  saleOrders = [],
  onOpenSaleOrder,
  onBack,
  onNew,
  onEdit,
  onCancelEdit,
  onSave,
  onPay,
  onConfirm,
  onPreview,
  onCreditNote,
  onSend,
  onAddLine,
  onUpdateLine,
  onRemoveLine,
  onChooseProduct,
  onStageChange,
  onPrint,
}) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("lines");

  const isPosted = invoice.status === "posted" || invoice.status === "paid" || invoice.payment_state === "paid";
  const currentStageId = isPosted ? "posted" : "draft";
  const handleStageSelect = (stage) => {
    if (!onStageChange) return;
    if (stage.id === currentStageId) return;
    if (stage.id === "posted" && !isPosted) onStageChange("post");
    else if (stage.id === "draft" && isPosted) onStageChange("draft");
  };

  // valeur d'affichage : form si éditable, sinon invoice.*
  const v = (key, fallback) => (editable && form ? form[key] : invoice[key] ?? fallback);
  const set = (key) => (val) => setForm?.((c) => ({ ...c, [key]: val }));

  const lines = editable && form ? form.lines : invoice.lines;

  const { untaxed, tax, total } = useMemo(() => {
    const u = lines.reduce((s, l) => s + (Number(l.subtotal) || Number(l.qty || l.quantity || 0) * Number(l.price_unit ?? l.price ?? 0) || 0), 0);
    const t = Math.round(u * 0.1925);
    return { untaxed: u, tax: t, total: u + t };
  }, [lines]);

  return (
    <div style={s.page}>
      {/* -------- Barre supérieure -------- */}
      <div style={s.topbar}>
        <div style={s.breadcrumb}>
          {saleOrders.length > 0 ? (
            <>
              <span style={{ cursor: "pointer", color: "#714b67" }} onClick={() => onOpenSaleOrder?.(saleOrders[0])}>Sales Orders</span>
              <span>/</span>
              <span style={{ cursor: "pointer", color: "#714b67" }} onClick={() => onOpenSaleOrder?.(saleOrders[0])}>{saleOrders[0].name}</span>
              <span>/</span>
              <strong>{invoice.number}</strong>
            </>
          ) : (
            <>
              <button type="button" onClick={onBack} style={s.back}>← Invoices</button>
              <span>/</span>
              <strong>{invoice.number}</strong>
            </>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {editable ? (
            <>
              <button type="button" onClick={onSave} style={s.btnPrimary}>SAVE</button>
              <button type="button" onClick={onCancelEdit} style={s.btnGhost}>DISCARD</button>
            </>
          ) : (
            <>
              {/* {onPrint && <button type="button" onClick={onPrint} style={{ ...s.btnGhost, border: "1px solid #d8dde2", padding: "6px 10px", borderRadius: 3 }}><Printer size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />Print</button>} */}
              {onEdit && (
                <button type="button" onClick={onEdit} style={{ ...s.btnGhost, border: "1px solid #d8dde2", padding: "6px 10px", borderRadius: 3 }}>{editable ? "Editing..." : "Edit"}</button>
              )}
              {onPreview && <button type="button" onClick={onPreview} style={{ ...s.btnGhost, border: "1px solid #d8dde2", padding: "6px 10px", borderRadius: 3 }}>Preview</button>}
              {onCreditNote && <button type="button" onClick={onCreditNote} style={{ ...s.btnGhost, border: "1px solid #d8dde2", padding: "6px 10px", borderRadius: 3 }}>Add Credit Note</button>}
              {onPay && <button type="button" onClick={onPay} style={{ ...s.btnPrimary, background: "#009688", opacity: isPosted ? 1 : 0.55 }} disabled={!isPosted}>PAY</button>}
              {onConfirm && <button type="button" onClick={onConfirm} style={{ ...s.btnPrimary, background: isPosted ? "#6c757d" : "#714b67", boxShadow: !isPosted ? "0 2px 8px rgba(113,75,103,.35)" : "none", opacity: isPosted ? 0.6 : 1 }} disabled={isPosted}>{isPosted ? "POSTED" : "CONFIRM"}</button>}
              {onNew && <button type="button" onClick={onNew} style={s.btnPrimary}><Plus size={12} /> New</button>}
            </>
          )}
        </div>
      </div>

      {/* -------- Smart buttons (Send retiré, gardé Sale Order) -------- */}
      <div style={{
        display: "flex",
        justifyContent: "flex-end",
        background: "#fff",
        borderBottom: "1px solid #d8dde2",
      }}>
        {saleOrders.length > 0 && (
          <SmartButton
            icon={FileText}
            count={saleOrders.length}
            label={saleOrders.length > 1 ? "Sale Orders" : "Sale Order"}
            onClick={() => onOpenSaleOrder?.(saleOrders[0])}
            color="#714b67"
          />
        )}
      </div>

      {/* -------- Étapes de statut cliquables -------- */}
      <div style={{ display: "flex", borderBottom: "1px solid #d8dde2", background: "#fff", fontSize: 10 }}>
        <div style={{ flex: "1 1 auto", maxWidth: 420, marginLeft: "auto", display: "flex" }}>
          <StageBar stages={INVOICE_STAGES} currentStageId={currentStageId} onSelect={handleStageSelect} />
        </div>
      </div>

      {/* -------- Feuille principale -------- */}
      <div style={s.sheet}>
        <h1 style={s.sheetTitle}>{invoice.number}</h1>

        {/* Champs d'en-tête */}
        <div style={s.twoCol}>
          <div>
            {editable ? (
              <label style={s.fieldRow}>
                <span style={s.fieldLabel}>Customer *</span>
                <CustomerSelect value={form?.partner_id ?? invoice.raw?.partner_id} onChange={(id) => setForm?.((c) => ({ ...c, partner_id: id }))} placeholder="Search customer… (tape 2 lettres)" />
              </label>
            ) : (
              <Field
                label="Customer *"
                editable={false}
                value={v("partner_id")}
              >
                {invoice.raw?.partner_id ? (
                  <span style={{ color: "#714b67", cursor: "pointer", textDecoration: "underline" }} onClick={() => navigate("/admin-panel/crm/customers")} title={`Partner #${invoice.raw.partner_id}`}>{invoice.customer}</span>
                ) : invoice.customer}
              </Field>
            )}
            <Field label="Ref Purchase order" editable={editable} value={v("ref", invoice.refPurchaseOrder)} onChange={set("ref")} />
            <Field label="Code Fournisseur" editable={editable} value={v("code_fournisseur", invoice.codeFournisseur)} onChange={set("code_fournisseur")} />
            <Field label="Billing Period" editable={editable} value={v("billing_period", invoice.billingPeriod)} onChange={set("billing_period")} />
            <Field label="Maturity Date" editable={editable} type="date" value={v("maturity_date", invoice.maturityDate)} onChange={set("maturity_date")} />
            <Field
              label="Early Payment"
              editable={editable}
              type="checkbox"
              value={v("early_payment_request_sent", invoice.earlyPaymentRequestSent)}
              onChange={set("early_payment_request_sent")}
              placeholder="Request Sent"
            >
              {invoice.earlyPaymentRequestSent ? "Request Sent" : "—"}
            </Field>
            <Field label="Early Req. Date" editable={editable} type="date" value={v("early_payment_request_date", invoice.earlyPaymentRequestDate)} onChange={set("early_payment_request_date")} />
            <Field label="Buyer" editable={editable} value={v("buyer", invoice.buyer)} onChange={set("buyer")} />
            <Field label="Delivery Address" editable={editable} value={v("delivery_address", invoice.deliveryAddress)} onChange={set("delivery_address")} />
          </div>

          <div>
            <Field label="Invoice Date *" editable={editable} type="date" value={v("date", invoice.invoiceDate)} onChange={set("date")} />
            <Field label="Sale order" editable={editable} value={v("sale_order", invoice.saleOrder)} onChange={set("sale_order")} />
            <Field label="Payment Reference" editable={editable} value={v("payment_reference", invoice.paymentReference)} onChange={set("payment_reference")} />
            <Field label="Project Budget" editable={editable} value={v("project_budget", invoice.projectBudget)} onChange={set("project_budget")} />
            <Field label="Due Date" editable={editable} type="date" value={v("due_date", invoice.dueDate)} onChange={set("due_date")} />
            <Field
              label="Journal"
              editable={editable}
              type={editable ? "select" : "text"}
              value={v("journal_id")}
              onChange={set("journal_id")}
              placeholder="Customer Invoices (default)"
              options={journals.map((j) => ({ value: j.id, label: `${j.name || j.code} — ${j.currency || "CFA"}` }))}
            >
              {invoice.journal}
            </Field>
          </div>
        </div>

        {/* Onglets */}
        <div style={s.tabs}>
          <span style={tab === "lines" ? s.tabActive : s.tab} onClick={() => setTab("lines")}>Invoice Lines</span>
          <span style={tab === "journal" ? s.tabActive : s.tab} onClick={() => setTab("journal")}>Journal Items</span>
          <span style={tab === "other" ? s.tabActive : s.tab} onClick={() => setTab("other")}>Other Info</span>
        </div>

        {/* ---------- Onglet Invoice Lines ---------- */}
        {tab === "lines" && (
          <>
            {editable ? (
              <div style={{ marginTop: 12 }}>
                <div style={s.tableHead}>
                  <span>Product</span><span>Label</span><span>Quantity</span><span>Unit Price</span><span>Subtotal</span><span />
                </div>
                {lines.map((line, index) => (
                  <div style={s.tableRow} key={index}>
                    <select
                      value={line.product_id}
                      onChange={(e) => onChooseProduct?.(index, e.target.value)}
                      style={s.cellInput}
                    >
                      <option value="">Add a product</option>
                      {products.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                    <input value={line.name || ""} onChange={(e) => onUpdateLine?.(index, { name: e.target.value })} placeholder="Description" style={s.cellInput} />
                    <input type="number" min="1" value={line.qty} onChange={(e) => onUpdateLine?.(index, { qty: e.target.value })} style={s.cellInput} />
                    <input type="number" min="0" value={line.price_unit} onChange={(e) => onUpdateLine?.(index, { price_unit: e.target.value })} style={s.cellInput} />
                    <strong style={s.amount}>{fmt.money(Number(line.qty || 0) * Number(line.price_unit || 0))}</strong>
                    <button type="button" onClick={() => onRemoveLine?.(index)} style={s.delete}><Trash2 size={13} /></button>
                  </div>
                ))}
                <div style={{ padding: "8px 6px", display: "flex", gap: 12 }}>
                  <button type="button" onClick={onAddLine} style={s.btnGhost}><Plus size={12} /> Add a product</button>
                  <span style={{ color: "#6f668d", fontSize: 11 }}>Add a section — Add a note</span>
                </div>
              </div>
            ) : (
              <div style={s.cardGrid}>
                {lines.map((l, i) => (
                  <div style={s.card} key={i}>
                    <div style={s.cardImg} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>{l.product_id ? <span style={{ color: "#714b67", cursor: "pointer", textDecoration: "underline" }} onClick={() => navigate("/admin-panel/products")} title={`Product #${l.product_id}`}>{l.product}</span> : l.product}</div>
                      <div style={{ color: "#63758a" }}>Quantity: {Number(l.qty).toFixed(2)} {l.uom}</div>
                      <div style={{ color: "#63758a" }}>Unit Price: {Number(l.price).toFixed(2)}</div>
                    </div>
                    <div style={s.amount}>{fmt.money(l.subtotal)}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ---------- Onglet Journal Items ---------- */}
        {tab === "journal" && (
          <div style={s.jGrid}>
            {lines.map((l, i) => (
              <div style={s.jCard} key={i}>
                <div style={s.jAccount}>{l.account}</div>
                <div style={s.jLabel}>{l.label}</div>
                <div style={s.jTaxLine}>
                  <span><span style={s.dot} />{l.tax}</span>
                  <span>{fmt.money(l.subtotal)} (CR)</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ---------- Onglet Other Info ---------- */}
        {tab === "other" && (
          <div style={s.twoCol}>
            <div>
              <div style={s.sectionTitle}>Invoice</div>
              <Field label="Customer Reference" editable={editable} value={v("ref", invoice.refPurchaseOrder)} onChange={set("ref")} />
              <Field label="Salesperson" editable={editable} value={v("salesperson", invoice.salesperson)} onChange={set("salesperson")} />
              <Field label="Sales Team" editable={editable} value={v("sales_team", invoice.salesTeam || "AFS GLOBAL")} onChange={set("sales_team")} />
              <Field label="Recipient Bank" editable={editable} value={v("recipient_bank", invoice.recipientBank || "40007300011 - BGFI BANK")} onChange={set("recipient_bank")} />
            </div>
            <div>
              <div style={s.sectionTitle}>Accounting</div>
              <Field label="Company" editable={false} value="Africa Systems DBA Lewoo sarl" />
              <Field label="Incoterm" editable={editable} value={v("incoterm", invoice.incoterm)} onChange={set("incoterm")} />
              <Field label="Fiscal Position" editable={editable} value={v("fiscal_position", invoice.fiscalPosition)} onChange={set("fiscal_position")} />
              <Field
                label="Auto-post"
                editable={editable}
                type={editable ? "select" : "text"}
                value={v("auto_post", invoice.autoPost || "no")}
                onChange={set("auto_post")}
                options={[{ value: "no", label: "No" }, { value: "yes", label: "Yes" }]}
              >
                {invoice.autoPost === "yes" ? "Yes" : "No"}
              </Field>
              <Field label="To Check" editable={editable} type="checkbox" value={v("to_check", invoice.toCheck)} onChange={set("to_check")} placeholder="To Check">
                {invoice.toCheck ? "Yes" : "No"}
              </Field>
            </div>
          </div>
        )}

        {/* Totaux — recalculés live sur lines (form ou invoice) */}
        <div style={s.totals}>
          <span>Untaxed: {fmt.money(untaxed)}</span>
          <span>Tax: {fmt.money(tax)}</span>
          <strong>Total: {fmt.money(total)}</strong>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", fontSize: 12, marginTop: 6 }}>
          <span style={{ marginRight: 12, color: "#526170" }}>Fee Applied:</span>
          <strong>{fmt.money(invoice.feeApplied ?? 0)}</strong>
        </div>

        {/* Conditions */}
        <div style={s.terms}>
          Terms &amp; Conditions:{" "}
          <a href={invoice.termsUrl} style={{ color: "#16869b" }}>{invoice.termsUrl}</a>
        </div>
      </div>
    </div>
  );
}
